import useMarginSelectors from "@shared/features/bots/BotForm/MultiSymbolMarginSelector/useMarginSelectors.ts";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid.tsx";
import { FormFieldTitle } from "features/bots/common/styles.ts";
import OverrideTypeToggle from "features/bots/BotForm/common/OverrideTypeToggle.tsx";
import HighlightedText from "common/Text/HighlightedText.tsx";
import React from "react";
import Flex from "common/Boxes/Flex.tsx";
import styled, { useTheme } from "styled-components";
import { NumberOption } from "@shared/types";
import Select from "common/Select/Select.tsx";
import useMedia from "utils/hooks/useMedia.ts";
import { BOT_MAX_LEVERAGE_WARNING_MESSAGE } from "@shared/features/bots/BotForm/common/constants.ts";

interface MarginSelectorsProps {
  isGlobalSettings?: boolean;
}

const MarginSelectors = ({ isGlobalSettings }: MarginSelectorsProps) => {
  const theme = useTheme();
  const {
    marginTypeSelectorData,
    multiplierSelectorData,
    leverageTypeSelectorData,
    hasMaxMultiplierError,
    leverageModifiedError,
    isViewOnly,
  } = useMarginSelectors(isGlobalSettings);
  const numColumns = useMedia([`(max-width: 500px)`], [1], 2);

  const getParsedOptions = (options: NumberOption[] | undefined) => {
    return options?.map((option) => ({
      value: option.value,
      label: option.name,
    }));
  };

  const marginTypeSelector = (
    <Select
      style={{ width: "100%" }}
      value={marginTypeSelectorData.value}
      onChange={marginTypeSelectorData.onChange}
      popupMatchSelectWidth={false}
      optionLabelProp="label"
      placeholder={marginTypeSelectorData.placeholder}
      options={getParsedOptions(marginTypeSelectorData.options)}
      mobileTitle={marginTypeSelectorData.title}
    />
  );

  const leverageTypeSelector = (
    <StyledLeverageTypeSelect
      value={leverageTypeSelectorData.value}
      onChange={leverageTypeSelectorData.onChange as any}
      optionLabelProp="label"
      placeholder={leverageTypeSelectorData.placeholder}
      bordered={false}
      options={getParsedOptions(leverageTypeSelectorData.options)}
      mobileTitle={leverageTypeSelectorData.title}
    />
  );

  const multiplierSelector = (
    <StyledLeverageTypeSelect
      value={multiplierSelectorData.value}
      onChange={multiplierSelectorData.onChange as any}
      popupMatchSelectWidth={false}
      optionLabelProp="label"
      placeholder={multiplierSelectorData.placeholder}
      bordered={false}
      $isRightInput={true}
      options={getParsedOptions(multiplierSelectorData.options)}
      mobileTitle={multiplierSelectorData.title}
    />
  );

  return (
    <>
      {!isViewOnly && (
        <EqualColumnsGrid numColumns={numColumns} style={{ marginTop: 8 }}>
          <div>
            <FormFieldTitle>
              {marginTypeSelectorData.title}
              <OverrideTypeToggle
                {...marginTypeSelectorData.overrideToggleProps}
              />
            </FormFieldTitle>
            {marginTypeSelector}
          </div>
          <div>
            <FormFieldTitle>
              {multiplierSelectorData.title}
              <OverrideTypeToggle
                {...multiplierSelectorData.overrideToggleProps}
              />
            </FormFieldTitle>
            <CombinedInputsWrapper>
              {leverageTypeSelector}
              {multiplierSelector}
            </CombinedInputsWrapper>
          </div>
        </EqualColumnsGrid>
      )}
      {leverageModifiedError && (
        <HighlightedText
          withBackgroundColor={false}
          color={theme.colors.darkYellow}
          style={{
            padding: "12px 0 8px 0",
          }}
        >
          {leverageModifiedError}
        </HighlightedText>
      )}
      {hasMaxMultiplierError && (
        <HighlightedText
          withBackgroundColor={false}
          color={theme.colors.darkYellow}
          style={{
            marginTop: leverageModifiedError ? 4 : 12,
            paddingBottom: 8,
          }}
        >
          {BOT_MAX_LEVERAGE_WARNING_MESSAGE}
        </HighlightedText>
      )}
    </>
  );
};

export default MarginSelectors;

const StyledLeverageTypeSelect = styled(Select)<{ $isRightInput?: boolean }>`
  width: 50%;
  ${(p) => p.$isRightInput && `border-left: ${p.theme.borders.light}`};
`;
const CombinedInputsWrapper = styled(Flex)`
  border: ${(p) => p.theme.borders.light};
  background-color: ${(p) => p.theme.colors.cardBgColor};
  border-radius: 8px;
  width: 100%;
`;
